import React, {useState, useEffect, useCallback, useRef} from 'react';
import Api from '@api';
import axios from 'axios';
import audiourl from '../../assets/audio/hooter.mp3';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import SOSPOPUP from './index';
import CloseIcon from '@mui/icons-material/Close';
import {IconButton} from '@mui/material';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import {subscribe, unsubscribe} from '@common/socketUtil';
const useAudio = (url) => {
  const [audio] = useState(new Audio(url));
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(audio);

  // Preload and prepare audio
  useEffect(() => {
    audioRef.current.load();
    audioRef.current.volume = 1.0;
    audioRef.current.loop = false;
  }, []);

  const toggle = useCallback(() => {
    try {
      // Reset audio to beginning
      audioRef.current.currentTime = 0;
      // Play audio with error handling
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setPlaying(true);
            console.log('Audio started playing successfully');
          })
          .catch((error) => {
            console.error('Error playing audio:', error);
            setPlaying(false);
            // Try alternative method for autoplay
            if (error.name === 'NotAllowedError') {
              console.log('Autoplay blocked, trying alternative method...');
              // Create a new audio context and try to play
              const audioContext = new (window.AudioContext || window.webkitAudioContext)();
              audioContext.resume().then(() => {
                const source = audioContext.createBufferSource();
                fetch(url)
                  .then(response => response.arrayBuffer())
                  .then(buffer => {
                    audioContext.decodeAudioData(buffer, (decodedData) => {
                      if (decodedData) {
                        source.buffer = decodedData;
                        source.connect(audioContext.destination);
                        source.start(0);
                        setPlaying(true);
                        console.log('Audio played via AudioContext');
                      }
                    });
                  })
                  .catch(err => console.error('Error with AudioContext:', err));
              });
            }
          });
      }
    } catch (error) {
      console.error('Error in toggle function:', error);
      setPlaying(false);
    }
  }, [url]);
  
  useEffect(() => {
    const handleEnded = () => setPlaying(false);
    audioRef.current.addEventListener('ended', handleEnded);
    return () => {
      audioRef.current.removeEventListener('ended', handleEnded);
    };
  }, []);
  
  return [playing, toggle];
};

function SOSPopup({getSSOPassengers}) {
  const [empInfo, setEmpInfo] = useState(null);
  const [tripInfo, setTripInfo] = useState(null);
  const [sosTrg, setSosTrg] = useState({});
  const [openDialog, setopenDialog] = useState(false);
  const [flag, setFlag] = useState();

  const [playing, toggle] = useAudio(audiourl);

  // Initialize audio context on component mount
  useEffect(() => {
    // Request notification permission
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    // Try to enable audio context immediately
    const enableAudioContext = async () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
          console.log('Audio context enabled');
        }
      } catch (error) {
        console.log('Audio context initialization failed:', error);
      }
    };
    
    // Create a silent audio element to enable audio playback
    const enableAudioPlayback = () => {
      try {
        const silentAudio = new Audio();
        silentAudio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
        silentAudio.volume = 0;
        silentAudio.play().then(() => {
          console.log('Audio playback enabled');
        }).catch(err => {
          console.log('Silent audio failed:', err);
        });
      } catch (error) {
        console.log('Audio playback initialization failed:', error);
      }
    };
    
    enableAudioContext();
    enableAudioPlayback();
    

  }, []);

  // Simple cross-tab close sync using storage event
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key !== 'sos-modal-close' || !e.newValue) return;
      setopenDialog(false);
      setSosTrg({});
      setEmpInfo(null);
      setTripInfo(null);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const broadcastModalClose = useCallback(() => {
    try {
      localStorage.setItem('sos-modal-close', String(Date.now()));
    } catch (_) {}
  }, []);

  const playHooter = useCallback(() => {
    try {
      // Only play if not already playing to avoid duplicates
      if (!playing) {
        toggle();
      }
    } catch (error) {
      console.error('Error triggering hooter:', error);
    }
  }, [toggle, playing]);

  const handleSosTrigger = useCallback((data) => {
    if(data.type === 'sos-trigger') {
      setSosTrg(data);
      playHooter();
      getSSOPassengers();
    }
  }, [playHooter, getSSOPassengers]);
  
  // subscribe to SOS trigger updates
  useEffect(() => {
    subscribe(handleSosTrigger);
    return () => {
      unsubscribe(handleSosTrigger);
    };
  }, [handleSosTrigger]);

  useEffect(() => {
    if(sosTrg?.raisedBy === 'DRIVER') {
      setFlag('driverId');
    }
    if(sosTrg?.raisedBy === 'EMPLOYEE') {
      setFlag('empId');
    }
  }, [sosTrg]);

  useEffect(() => {
    if (flag == 'empId') {
      axios
        .get(Api.employee.list + '/' + sosTrg?.employeeId)
        .then((res) => {
          if (res?.data?.status == '200') {
            setEmpInfo(res?.data?.data);
          }
        })
        .catch((err) => {
          console.error('err', err);
        });
    }

    if (flag == 'driverId') {
      axios
        .get(Api.driver.list + '/' + sosTrg?.driverId)
        .then((res) => {
          if (res?.data?.status == '200') {
            setEmpInfo(res?.data?.data);
          }
        })
        .catch((err) => {
          console.log('err', err);
        });
    }
  }, [flag, sosTrg]);

  useEffect(() => {
    if (sosTrg?.tripId) {
      axios
        .get(
          Api.baseUri +
            `/user-reg/trip-route/get-trip-by-id?tripId=${sosTrg?.tripId}`,
        )
        .then((res) => {
          if (res?.data?.status == '200') {
            setTripInfo(res?.data?.data);
          }
        })
        .catch((err) => {});
    }
  }, [sosTrg?.tripId]);

  useEffect(() => {
    if (
      isArray(tripInfo) &&
      !isEmpty(tripInfo) &&
      isObject(empInfo) &&
      !openDialog
    ) {
      setopenDialog(true);
    }
  }, [tripInfo, empInfo]);

  return (
    <>
      {openDialog && (
        <Dialog
          open={openDialog}
          maxWidth='false'
          PaperProps={{
            sx: {
              width: '500px',
            },
          }}
          style={{borderRadius: '4rem'}}
        >
          <div>
            <DialogContent style={{padding: '0px'}}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  background: '#f5f2f2',
                  height: '3rem',
                  paddingRight: '1.5rem',
                  paddingLeft: '1.5rem',
                  position: 'fixed',
                  width: '500px',
                  zIndex: '9',
                  borderRadius: '5px 5px 0px 0px',
                }}
              >
                <h1 style={{marginTop: '0.8rem'}}>SOS Details</h1>
                <IconButton
                  aria-label='close'
                  onClick={() => {
                    setopenDialog(false);
                    setSosTrg({});
                    setEmpInfo(null);
                    setTripInfo(null);
                    broadcastModalClose();
                  }}
                  style={{marginTop: '0.5rem'}}
                >
                  <CloseIcon />
                </IconButton>
              </div>
              <div style={{marginTop: '50px'}}>
                <SOSPOPUP
                  tripInfo={tripInfo}
                  empInfo={empInfo}
                  keys={flag}
                  sosData={sosTrg}
                  setopenDialog={setopenDialog}
                  closePopup = {()=>{                    
                    broadcastModalClose();
                  }}
                />
              </div>
            </DialogContent>
          </div>
        </Dialog>
      )}
    </>
  );
}

export default SOSPopup;